import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  connectRefinementList,
  connectToggleRefinement,
  connectCurrentRefinements,
} from 'react-instantsearch-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Tooltip from '@material-ui/core/Tooltip'
import CheckIcon from '@material-ui/icons/Check'
import {
  Calendar,
  ChevronDown,
  CircleUserRound,
  Heart,
  Trophy,
  Users,
  X,
  COACH_PROFILE_FILTER_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import {
  BOOK_ONLINE_FILTER_LABEL,
  BOOK_THIS_WEEK_FILTER_LABEL,
} from '@lib/coaches-directory/book-this-week'
import {
  SECTION_TITLES,
  buildBundles,
  valuesForBundle,
  COACHING_FORMAT_OPTIONS,
  DIRECTORY_REFINER_ATTRIBUTES,
} from './refiner-filter-bundles'
import { useStyles } from './RefinersToolbar.styles'

function FilterMenuSectionTitle({ children }) {
  const classes = useStyles()
  return (
    <MenuItem disabled className={classes.filterMenuSectionTitle} disableGutters>
      <Typography component="span" variant="caption">{children}</Typography>
    </MenuItem>
  )
}

FilterMenuSectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
}

function FilterMenuOption({
  label, selected, disabled, onSelect, showCheck = true,
}) {
  const classes = useStyles()
  return (
    <MenuItem
      selected={selected}
      disabled={disabled}
      onClick={onSelect}
      className={classes.filterMenuItem}
      classes={{ selected: classes.filterMenuItemSelected }}
    >
      {showCheck && (
        <ListItemIcon className={classes.filterMenuLeading}>
          {selected
            ? <CheckIcon fontSize="small" data-testid="filter-menu-check" />
            : <span className={classes.filterMenuLeadingSpacer} aria-hidden />}
        </ListItemIcon>
      )}
      <Typography component="span" variant="body2">{label}</Typography>
    </MenuItem>
  )
}

FilterMenuOption.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  showCheck: PropTypes.bool,
}

function FilterPillMenuButton({
  icon: Icon,
  label,
  shortLabel,
  isActive,
  children,
}) {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        className={[
          classes.filterPillButton,
          isActive && classes.filterPillButtonActive,
        ].filter(Boolean).join(' ')}
        startIcon={<Icon {...COACH_PROFILE_FILTER_ICON_PROPS} />}
        endIcon={(
          <ChevronDown
            {...COACH_PROFILE_FILTER_ICON_PROPS}
            data-testid="filter-pill-caret"
            className={[
              classes.filterPillCaret,
              Boolean(anchorEl) && classes.filterPillCaretOpen,
            ].filter(Boolean).join(' ')}
          />
        )}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {shortLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        getContentAnchorEl={null}
        PaperProps={{ className: classes.filterMenuPaper }}
      >
        {typeof children === 'function' ? children({ onClose: () => setAnchorEl(null) }) : children}
      </Menu>
    </>
  )
}

FilterPillMenuButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  shortLabel: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
}

function AvailabilityFilterMenu({ currentRefinement, refine }) {
  return (
    <FilterMenuOption
      label={BOOK_ONLINE_FILTER_LABEL}
      selected={Boolean(currentRefinement)}
      onSelect={() => refine(!currentRefinement)}
    />
  )
}

AvailabilityFilterMenu.propTypes = {
  currentRefinement: PropTypes.bool,
  refine: PropTypes.func.isRequired,
}

const ConnectedAvailabilityFilterMenu = connectToggleRefinement(AvailabilityFilterMenu)

function CoachingFormatFilterMenu({ items, refine, currentRefinement }) {
  return (
    <>
      <FilterMenuSectionTitle>{SECTION_TITLES.coachingFormat.toUpperCase()}</FilterMenuSectionTitle>
      {COACHING_FORMAT_OPTIONS.map((option) => {
        const refiner = items.find((item) => item.label === option.value)
        const selected = currentRefinement.includes(option.value)
        return (
          <FilterMenuOption
            key={option.value}
            label={option.label}
            selected={selected}
            disabled={!refiner || refiner.count === 0}
            onSelect={() => {
              if (refiner) refine(refiner.value)
            }}
          />
        )
      })}
    </>
  )
}

CoachingFormatFilterMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
}

const ConnectedCoachingFormatFilterMenu = connectRefinementList(CoachingFormatFilterMenu)

function WomenCoachesFilterMenu({ items, refine, currentRefinement }) {
  const refiner = items.find((item) => item.label === 'F')
  const activeValues = Array.isArray(currentRefinement)
    ? currentRefinement
    : [currentRefinement].filter((value) => value != null && value !== '')
  const selected = activeValues.includes('F')
  return (
    <FilterMenuOption
      label="Women Coaches"
      selected={selected}
      disabled={!refiner || refiner.count === 0}
      onSelect={() => {
        if (refiner) refine(refiner.value)
      }}
    />
  )
}

WomenCoachesFilterMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
}

const ConnectedWomenCoachesFilterMenu = connectRefinementList(WomenCoachesFilterMenu)

function PgaMemberFilterMenu({
  items, refine, currentRefinement, refinersOptions,
}) {
  const refiners = items.filter((item) => refinersOptions.includes(item.label))
  const selectedValues = currentRefinement.filter((value) => refinersOptions.includes(value))
  const selected = currentRefinement.length > 0
    && selectedValues.length === refinersOptions.length
    && refinersOptions.length > 0

  return (
    <FilterMenuOption
      label="PGA Member"
      selected={selected}
      disabled={refiners.length === 0}
      onSelect={() => {
        if (selected) {
          refine(currentRefinement.filter((value) => !refinersOptions.includes(value)))
          return
        }
        refine([...new Set([...currentRefinement, ...refinersOptions])])
      }}
    />
  )
}

PgaMemberFilterMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  refinersOptions: PropTypes.arrayOf(PropTypes.string),
}

const ConnectedPgaMemberFilterMenu = connectRefinementList(PgaMemberFilterMenu)

function CoachAttributesFilterMenu({ memberTypeOptions }) {
  return (
    <>
      <FilterMenuSectionTitle>{SECTION_TITLES.coachAttributes.toUpperCase()}</FilterMenuSectionTitle>
      <ConnectedWomenCoachesFilterMenu attribute="gender" operator="and" />
      <ConnectedPgaMemberFilterMenu
        attribute="memberType"
        operator="or"
        limit={50}
        refinersOptions={memberTypeOptions}
      />
    </>
  )
}

CoachAttributesFilterMenu.propTypes = {
  memberTypeOptions: PropTypes.arrayOf(PropTypes.string),
}

function BundleFilterMenuOption({
  items, refine, currentRefinement, refinersOptions, label,
}) {
  const refiners = items.filter((item) => refinersOptions.includes(item.label))
  const selectedValues = currentRefinement.filter((value) => refinersOptions.includes(value))
  const selected = currentRefinement.length > 0
    && selectedValues.length === refinersOptions.length

  const handleSelect = () => {
    if (selected) {
      refine(currentRefinement.filter((value) => !refinersOptions.includes(value)))
      return
    }
    refine([...new Set([...currentRefinement, ...refinersOptions])])
  }

  return (
    <FilterMenuOption
      label={label}
      selected={selected}
      disabled={refiners.length === 0}
      onSelect={handleSelect}
    />
  )
}

BundleFilterMenuOption.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  refinersOptions: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string.isRequired,
}

function GolferAudienceServiceMenu({
  items, refine, currentRefinement, adultsOptions, juniorsOptions,
}) {
  return (
    <>
      <BundleFilterMenuOption
        items={items}
        refine={refine}
        currentRefinement={currentRefinement}
        refinersOptions={adultsOptions}
        label="Adults"
      />
      <BundleFilterMenuOption
        items={items}
        refine={refine}
        currentRefinement={currentRefinement}
        refinersOptions={juniorsOptions}
        label="Juniors"
      />
    </>
  )
}

GolferAudienceServiceMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  adultsOptions: PropTypes.arrayOf(PropTypes.string),
  juniorsOptions: PropTypes.arrayOf(PropTypes.string),
}

const ConnectedGolferAudienceServiceMenu = connectRefinementList(GolferAudienceServiceMenu)

function GolferAudienceExperienceMenu({
  items, refine, currentRefinement, beginnersOptions,
}) {
  if (beginnersOptions.length === 0) return null
  return (
    <BundleFilterMenuOption
      items={items}
      refine={refine}
      currentRefinement={currentRefinement}
      refinersOptions={beginnersOptions}
      label="Beginners"
    />
  )
}

GolferAudienceExperienceMenu.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  currentRefinement: PropTypes.array,
  beginnersOptions: PropTypes.arrayOf(PropTypes.string),
}

const ConnectedGolferAudienceExperienceMenu = connectRefinementList(GolferAudienceExperienceMenu)

function GolferAudienceFilterMenu({
  adultsOptions, juniorsOptions, beginnersOptions,
}) {
  return (
    <>
      <FilterMenuSectionTitle>{SECTION_TITLES.golferAudience.toUpperCase()}</FilterMenuSectionTitle>
      <ConnectedGolferAudienceServiceMenu
        attribute="serviceTypes.services.serviceName"
        operator="or"
        limit={50}
        adultsOptions={adultsOptions}
        juniorsOptions={juniorsOptions}
      />
      <ConnectedGolferAudienceExperienceMenu
        attribute="experienceTypes.experiences.experienceName"
        operator="or"
        limit={50}
        beginnersOptions={beginnersOptions}
      />
    </>
  )
}

GolferAudienceFilterMenu.propTypes = {
  adultsOptions: PropTypes.arrayOf(PropTypes.string),
  juniorsOptions: PropTypes.arrayOf(PropTypes.string),
  beginnersOptions: PropTypes.arrayOf(PropTypes.string),
}

const REFINER_ATTRIBUTES = new Set(DIRECTORY_REFINER_ATTRIBUTES)

const collectSelectedValues = (items = []) => {
  const out = new Map()
  items
    .filter((item) => REFINER_ATTRIBUTES.has(item.attribute))
    .forEach((item) => {
      if (!out.has(item.attribute)) out.set(item.attribute, new Set())
      const set = out.get(item.attribute)
      if (Array.isArray(item.items) && item.items.length > 0) {
        item.items.forEach((sub) => {
          const value = sub.label != null ? sub.label : sub.value
          if (value != null) set.add(String(value))
        })
      } else if (item.currentRefinement != null && item.currentRefinement !== '') {
        if (Array.isArray(item.currentRefinement)) {
          item.currentRefinement.forEach((value) => set.add(String(value)))
        } else {
          set.add(String(item.currentRefinement))
        }
      }
    })
  return out
}

const isBundleActive = (selectedValues, bundle) => {
  if (!bundle) return false
  const set = selectedValues.get(bundle.attribute)
  if (!set) return false
  return bundle.values.some((value) => set.has(String(value)))
}

function RefinersToolbarMenus({
  items,
  refine,
  refinerData,
  savedOnly,
  onToggleSavedOnly,
  savedCoachCount,
  selectedPersonaId,
  bookThisWeek,
  onToggleBookThisWeek,
  onClearFilters,
  onClearDirectoryFilters,
}) {
  const classes = useStyles()
  const bundles = buildBundles(refinerData)
  const selectedValues = collectSelectedValues(items)
  const adultsOptions = valuesForBundle(bundles, 'adults')
  const juniorsOptions = valuesForBundle(bundles, 'juniors')
  const memberTypeOptions = valuesForBundle(bundles, 'pgaMember')
  const beginnersOptions = valuesForBundle(bundles, 'beginners')

  const bookOnlineActive = isBundleActive(selectedValues, bundles.find((bundle) => bundle.id === 'bookOnline'))
  const availabilityActive = bookOnlineActive || Boolean(bookThisWeek)
  const coachingFormatActive = ['oneToOne', 'group'].some((id) => isBundleActive(selectedValues, bundles.find((bundle) => bundle.id === id)))
  const coachAttributesActive = ['womenCoaches', 'pgaMember'].some((id) => isBundleActive(selectedValues, bundles.find((bundle) => bundle.id === id)))
  const golferAudienceActive = ['adults', 'juniors', 'beginners'].some((id) => isBundleActive(selectedValues, bundles.find((bundle) => bundle.id === id)))
  const refinementItems = (items || []).filter((item) => REFINER_ATTRIBUTES.has(item.attribute))
  const hasActiveFilters = Boolean(selectedPersonaId)
    || Boolean(savedOnly)
    || availabilityActive
    || coachingFormatActive
    || coachAttributesActive
    || golferAudienceActive
    || refinementItems.length > 0

  const handleClearFilters = () => {
    if (onClearDirectoryFilters) {
      onClearDirectoryFilters()
    } else {
      if (refinementItems.length > 0) {
        refine?.(refinementItems)
      }
      if (selectedPersonaId) {
        onClearFilters?.()
      }
      if (bookThisWeek) {
        onToggleBookThisWeek?.()
      }
    }
    if (savedOnly) {
      onToggleSavedOnly?.()
    }
  }

  return (
    <Box className={classes.filterToolbar}>
      <Box className={classes.filterIconGroup}>
        <FilterPillMenuButton
          icon={Calendar}
          label="Availability filters"
          shortLabel="Availability"
          isActive={availabilityActive}
        >
          <>
            <FilterMenuSectionTitle>{SECTION_TITLES.availability.toUpperCase()}</FilterMenuSectionTitle>
            <FilterMenuOption
              label={BOOK_THIS_WEEK_FILTER_LABEL}
              selected={Boolean(bookThisWeek)}
              onSelect={() => onToggleBookThisWeek?.()}
            />
            <ConnectedAvailabilityFilterMenu attribute="bookable" value />
          </>
        </FilterPillMenuButton>

        <FilterPillMenuButton
          icon={Trophy}
          label="Coach attribute filters"
          shortLabel="Attributes"
          isActive={coachAttributesActive}
        >
          <CoachAttributesFilterMenu memberTypeOptions={memberTypeOptions} />
        </FilterPillMenuButton>

        <FilterPillMenuButton
          icon={Users}
          label="Coaching format filters"
          shortLabel="Format"
          isActive={coachingFormatActive}
        >
          <ConnectedCoachingFormatFilterMenu
            attribute="serviceTypes.services.serviceName"
            operator="or"
          />
        </FilterPillMenuButton>

        <FilterPillMenuButton
          icon={CircleUserRound}
          label="Golfer audience filters"
          shortLabel="Audience"
          isActive={golferAudienceActive}
        >
          <GolferAudienceFilterMenu
            adultsOptions={adultsOptions}
            juniorsOptions={juniorsOptions}
            beginnersOptions={beginnersOptions}
          />
        </FilterPillMenuButton>

        <Divider
          className={classes.filterToolbarDivider}
          orientation="vertical"
          flexItem
        />

        <Tooltip
          title={savedOnly
            ? 'Show all coaches'
            : `Show only saved coaches${savedCoachCount > 0 ? ` (${savedCoachCount})` : ''}`}
        >
          <span className={classes.savedCoachesToggleWrap}>
            <Button
              type="button"
              variant="text"
              disableRipple
              aria-label={savedOnly
                ? 'Saved coaches filter is on. Turn off to show all coaches.'
                : 'Show only saved coaches'}
              aria-pressed={Boolean(savedOnly)}
              disabled={!savedOnly && savedCoachCount === 0}
              className={[
                classes.iconFilterCircle,
                savedOnly && classes.iconFilterCircleActive,
              ].filter(Boolean).join(' ')}
              startIcon={(
                <Heart
                  {...COACH_PROFILE_FILTER_ICON_PROPS}
                  fill={savedOnly ? 'currentColor' : 'none'}
                />
              )}
              onClick={onToggleSavedOnly}
            />
          </span>
        </Tooltip>

        <Tooltip title={hasActiveFilters ? 'Clear filters' : 'No filters to clear'}>
          <span className={classes.savedCoachesToggleWrap}>
            <Button
              type="button"
              variant="text"
              disableRipple
              aria-label="Clear filters"
              disabled={!hasActiveFilters}
              className={[
                classes.iconFilterCircle,
                hasActiveFilters && classes.iconFilterCircleActive,
              ].filter(Boolean).join(' ')}
              startIcon={<X {...COACH_PROFILE_FILTER_ICON_PROPS} />}
              onClick={handleClearFilters}
            />
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

RefinersToolbarMenus.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  refinerData: PropTypes.object,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func,
  savedCoachCount: PropTypes.number,
  selectedPersonaId: PropTypes.string,
  bookThisWeek: PropTypes.bool,
  onToggleBookThisWeek: PropTypes.func,
  onClearFilters: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
}

const ConnectedRefinersToolbarMenus = connectCurrentRefinements(RefinersToolbarMenus)

function RefinersToolbar(props) {
  return <ConnectedRefinersToolbarMenus {...props} />
}

RefinersToolbar.propTypes = {
  refinerData: PropTypes.object,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func,
  savedCoachCount: PropTypes.number,
  selectedPersonaId: PropTypes.string,
  bookThisWeek: PropTypes.bool,
  onToggleBookThisWeek: PropTypes.func,
  onClearFilters: PropTypes.func,
  onClearDirectoryFilters: PropTypes.func,
}

export default RefinersToolbar
